(function (global, factory) {
  'use strict';

  if (typeof define === 'function' && define.amd) {
    // If supports for AMD is available return the value to the AMD Loader
    // and to the global (window) object in case any JS module is expecting
    // the CurrentLocationComponentModule to exists on the window object.
    define([], () => {
      global.CurrentLocationComponent = factory(global);
      return global.CurrentLocationComponent;
    });
  } else if (typeof exports === 'object') {
    // If supports for Common JS is available export the module
    // and pass its value to the global (window) object in case non Common JS code
    // is expected to work with this CurrentLocationComponentModule.
    const handler = factory(global);
    global.CurrentLocationComponent = handler;
    module.exports = handler;
  } else {
    // If not support for AMD or Common JS is available register the module in the global scope.
    global.CurrentLocationComponent = factory(global);
  }
}(typeof window !== 'undefined' ? window : {}, (global) => {
  'use strict';

  global.NewHomeSource = global.NewHomeSource || {};
  const previousComponent = global.NewHomeSource.CurrentLocationComponent;

  /**
   * CurrentLocationComponent
   * Handles search input interactions in a reusable way.
   * Provides methods for event binding, input handling, and noConflict support.
   */
  class CurrentLocationComponent {
    /**
     * Creates a new CurrentLocationComponent instance.
     * Expects already queried DOM nodes (no internal querying done here).
     */
    constructor(config) {
      this.searchBarState = config.searchBarState;
      this.searchBarContainer = this.searchBarState.searchBarContainer;
      this.isMobile = this.searchBarState.isMobile;

      this.isOpenCurrentLocation = false;
      this.observers = [];

      const hasSearchText = this.searchBarState.searchText;
      this.searchText = hasSearchText ? hasSearchText.trim() : '';

      if (this.searchBarContainer) {
        this.currentLocationElement = this.searchBarContainer.querySelector('[data-current-location]');
        this.toggleCurrentLocation(!!this.searchText);

        this.bindEvents();
      }
    }

    /**
     * Subscribes a callback to be notified when the open status changes.
     * @param {function} callback - The callback function to be invoked on status change.
     */
    subscribeOpenStatus(callback) {
      this.observers.push(callback);
    }

    /**
     * Notifies all subscribed observers of a status change.
     * @param {boolean} value - The new open status value.
     */
    notifyObservers(value) {
      this.observers.forEach((callback) => callback(value));
    }

    /**
     * Sets the open status and notifies observers.
     * @param {boolean} value - The new open status value.
     */
    setOpenStatus(value) {
      this.isOpenCurrentLocation = value;
      this.notifyObservers(value);
    }

    /**
     * Toggles the visibility of the current location element.
     * @param {boolean} visibility - The visibility state to set.
     */
    toggleCurrentLocation(visibility) {
      if (this.currentLocationElement) {
        this.currentLocationElement.hidden = visibility;
        this.setOpenStatus(!visibility);
      }
    }

    /**
     * Binds event listeners for current location interactions.
     */
    bindEvents() {
      this.boundCurrentLocationOutsideClick = this.handleCurrentLocationOutsideClick.bind(this);
      document.addEventListener('click', this.boundCurrentLocationOutsideClick);

      this.boundCurrentLocationClick = CurrentLocationComponent.handleCurrentLocationClick.bind(this);
      this.currentLocationElement.addEventListener('click', this.boundCurrentLocationClick);
    }

    /**
     * Handles clicks outside the current location element to toggle its visibility.
     * @param {MouseEvent} event - The click event.
     */
    handleCurrentLocationOutsideClick(event) {
      if (!this.searchBarContainer) {
        return;
      }

      const clickTarget = event.target;

      const isClickInsideSearchBar = this.searchBarContainer.contains(clickTarget);
      const isSearchBarOutsideModal = this.searchBarContainer.querySelector('[data-mobile-search-bar-outside-modal]');
      const inputContainer = this.searchBarContainer.querySelector('[data-search-bar-input-container]');

      const shouldToggle = this.shouldToggleCurrentLocation({
        clickTarget,
        isClickInsideSearchBar,
        isSearchBarOutsideModal,
        inputContainer
      });

      this.toggleCurrentLocation(shouldToggle);
    }

    /**
     * Determines if the current location element should be toggled based on click context.
     * @param {Object} params - The parameters for the decision.
     * @param {HTMLElement} params.clickTarget - The element that was clicked.
     * @param {boolean} params.isClickInsideSearchBar - Whether the click was inside the search bar.
     * @param {boolean} params.isSearchBarOutsideModal - Whether the search bar is outside the modal.
     * @param {HTMLElement} params.inputContainer - The input container element.
     * @returns {boolean} - True if the current location element should be toggled, false otherwise.
     */
    shouldToggleCurrentLocation({
      clickTarget, isClickInsideSearchBar, isSearchBarOutsideModal, inputContainer
    }) {
      if (this.isMobile && !isSearchBarOutsideModal && inputContainer) {
        const isClickInsideInput = inputContainer.contains(clickTarget);
        const isClickInsideCurrentLocation = this.currentLocationElement.contains(clickTarget);
        const shouldShow = (isClickInsideCurrentLocation || isClickInsideInput) && !this.searchText;

        // We want to hide if shouldShow is false
        return !shouldShow;
      }

      // For non-mobile or clicks outside the search bar
      return this.searchText || !isClickInsideSearchBar;
    }

    /**
     * Handles clicks on the current location element.
     * Redirects to the map view with the user's current location if permission is granted.
     */
    static handleCurrentLocationClick() {
      CurrentLocationComponent.getCurrentLocation().then((locationData) => {
        const { status, boundingBoxString } = locationData;
        if (status === 'granted') {
          window.location.href = `/communities/map?coordinates=${boundingBoxString}`;
        }
      });
    }

    /**
     * Cleans up event listeners to prevent memory leaks.
     */
    destroyInstances() {
      if (this.boundCurrentLocationOutsideClick) {
        document.removeEventListener('click', this.boundCurrentLocationOutsideClick);
        this.boundCurrentLocationOutsideClick = null;
      }

      if (this.boundCurrentLocationClick && this.currentLocationElement) {
        this.currentLocationElement.removeEventListener('click', this.boundCurrentLocationClick);
        this.boundCurrentLocationClick = null;
      }
    }

    /**
     * Computes a realistic geographic bounding box around the user's location.
     * Expands by a defined radius in km in each direction.
     * Uses real-world geographic calculations instead of pixel-based approximations.
     * @param {number} latitude - The latitude of the center point.
     * @param {number} longitude - The longitude of the center point.
     * @returns {string} The bounding box string in the format "minLat,minLng,maxLat,maxLng".
     */
    static computeViewportBoundingBox(latitude, longitude) {
      // Constant for converting degrees to radians
      const DEG_TO_RAD = Math.PI / 180;

      // Approximate distance in kilometers for 1 degree of latitude
      const KM_PER_DEGREE_LAT = 111;

      // Approximate distance in kilometers for 1 degree of longitude at current latitude
      // Longitude degree varies by latitude (cosine scaling)
      const KM_PER_DEGREE_LNG = 111 * Math.cos(latitude * DEG_TO_RAD);

      // Radius of the bounding box in kilometers (distance from center to edge)
      const BOUNDING_BOX_RADIUS_KM = 9;

      // Convert km radius to degrees
      const deltaLat = BOUNDING_BOX_RADIUS_KM / KM_PER_DEGREE_LAT;
      const deltaLng = BOUNDING_BOX_RADIUS_KM / KM_PER_DEGREE_LNG;

      // Compute min/max latitude
      const minLatitude = latitude - deltaLat;
      const maxLatitude = latitude + deltaLat;

      // Compute min/max longitude
      const minLongitude = longitude - deltaLng;
      const maxLongitude = longitude + deltaLng;

      // Return the bounding box string in the expected format
      return `${minLatitude},${minLongitude},${maxLatitude},${maxLongitude}`;
    }

    /**
     * Gets the user's current location using the Geolocation API.
     * @returns {Promise<Object>} A promise that resolves to an object containing status, message, latitude,
     * longitude, and boundingBoxString.
     */
    static getCurrentLocation() {
      // If geolocation API is unavailable
      const geolocationDeniedMessage = 'Please enter a valid location or select an item from the list.';
      if (!navigator.geolocation) {
        return Promise.resolve({
          status: 'unsupported',
          message: geolocationDeniedMessage,
        });
      }

      return new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          ({ coords: { latitude, longitude } }) => {
            const boundingBoxString = CurrentLocationComponent.computeViewportBoundingBox(latitude, longitude);

            resolve({
              status: 'granted',
              message: 'Location permission granted.',
              latitude,
              longitude,
              boundingBoxString, // minLat,minLng,maxLat,maxLng
            });
          },
          () => {
            resolve({
              status: 'denied',
              message: geolocationDeniedMessage,
            });
          }
        );
      });
    }

    /**
     * Restores the previous definition of CurrentLocationComponent.
     * Useful when integrating with other scripts that might use the same namespace.
     * @returns {CurrentLocationComponent} The current CurrentLocationComponent class
     */
    static noConflict() {
      global.NewHomeSource.CurrentLocationComponent = previousComponent;
      return CurrentLocationComponent;
    }
  }

  global.NewHomeSource.CurrentLocationComponent = CurrentLocationComponent;
  return CurrentLocationComponent;
}));
